import { captureMessage } from "@sentry/react";

import { Notification, NotificationTargetKind } from "@/generated/graphql";
import { withNavigationSource } from "@/lib/navigation-source";

// This lives in its own module instead of inlined into `action-feed.tsx` so
// the routing table and its Sentry reporting for an unrecognised rule key
// are unit-testable without mounting the feed. See action-routes.test.ts.

// Destination for a notification without an `action.targetKind`. Those
// route to `/instructions/:taskKind` instead (see `routeForAction`). Keyed
// by `type` (the schema's stable identifier), mirroring the action verbs in
// pantheon's `Cadmus.ActionFeed.Student.Rules`: `not_in_group` → "Join a
// group", the due/idle/no-tasks rules → "Go to task board", `feedback` →
// "View feedback".
//
// `charter` is handled separately in `routeForAction` below. It needs a
// charter id, not a static path, and is deliberately absent from this
// table.
const RULE_KEY_ROUTES: Record<string, string> = {
  not_in_group: "/group",
  task_soon: "/home/taskboard",
  section_due: "/home/taskboard",
  task_overdue: "/home/taskboard",
  task_idle: "/home/taskboard",
  no_tasks: "/home/taskboard",
  feedback: "/home/feedback",
  not_submitted: "/home/writing",
  milestone_overdue: "/home/taskboard",
  sentiment_pending: "/home/sentiment-check",
};

// `NotificationTargetKind` also carries destinations no action in this feed
// produces yet: `CONTACT`, `GROUP_PAGE`, and `THREAD` belong to other
// notification surfaces, not this one. So this table is deliberately a
// partial map rather than a `Record<NotificationTargetKind, string>`. Only
// the three task-shaped kinds get a route; indexing with any other member
// returns `undefined`, falling through to `routeForAction`'s `type`-keyed
// branch below exactly like an unrecognised value always did. That also
// means a codegen regen that adds a fourth *task* kind no longer fails the
// build the way `tone`'s exhaustive switch does in action-card.tsx. The
// runtime fallback below is the only guard, the same gap `TaskKind`'s own
// fourth member, `VIVA`, already leaves unrouted today.
const TASK_KIND_ROUTES: Partial<Record<NotificationTargetKind, string>> = {
  [NotificationTargetKind.GroupTask]: "/instructions/group",
  [NotificationTargetKind.IndividualTask]: "/instructions/individual",
  // Peer differs from the two writing kinds: its actions ("Rate your group",
  // "Submit evaluation") need the peer-evaluation workflow, which lives at
  // `/home/peer` (the overview with the start/finalise controls). The
  // `/instructions/peer` sheet the other kinds' instructions link to is
  // read-only and has no path to actually rating anyone.
  [NotificationTargetKind.PeerTask]: "/home/peer",
};

// The "start writing" rule keys. They carry a work targetKind (GROUP_TASK /
// INDIVIDUAL_TASK) like `instructions` does, but must route to the writing hub
// rather than the read-only instruction sheet — see destinationFor.
const START_WRITING_RULE_KEYS = new Set<string>([
  "no_writing_group",
  "no_writing_individual",
  "write_idle_group",
  "write_idle_individual",
]);

function destinationFor(
  notification: Notification,
  charterId: string | null | undefined
): string {
  // The work-targeted rules split by intent, not by targetKind. `instructions`
  // (work never opened) wants the read-only sheet via TASK_KIND_ROUTES, so it
  // keeps the targetKind fallback below. The start-writing rules — `no_writing_*`
  // (opened, still empty) and `write_idle_*` (written, then gone quiet) — all say
  // "Open … work" and want the writing hub instead. Their shared targetKind can't
  // tell them apart from `instructions`, so they resolve by rule key here, ahead
  // of the targetKind fallback that would otherwise send them to the sheet. The
  // hub (not `/work/:workId/editing`) because the notification carries no workId
  // and group work may not have one yet — the same reason `not_submitted` targets it.
  if (START_WRITING_RULE_KEYS.has(notification.type)) return "/home/writing";

  // Each lookup is an independent fallback tried in order, not a mutually
  // exclusive branch: a non-task targetKind (GROUP_PAGE, CONTACT, THREAD)
  // misses TASK_KIND_ROUTES but must still reach the charter and rule-key
  // routes below, not skip straight to /home.
  const targetKind = notification.action?.targetKind;
  if (targetKind != null) {
    const mapped = TASK_KIND_ROUTES[targetKind];
    if (mapped != null) return mapped;
  }

  if (notification.type === "charter" && charterId != null) {
    // The charter action needs the id `StudentHome` already fetched via
    // `useWorkGroupQuery` (`workGroup.charter.id`), not a static path, so it
    // routes here rather than through `RULE_KEY_ROUTES`. With no charter yet,
    // `charterId` is undefined and this falls through to the rule-key and
    // `/home` fallbacks below.
    return `/charter/${charterId}`;
  }

  // Look up only the table's own keys. `RULE_KEY_ROUTES` is a plain object, so a
  // stray `type` that names an `Object.prototype` member (`constructor`,
  // `toString`, …) would otherwise resolve to an inherited function and escape
  // as a bogus route, throwing in `withNavigationSource` before the Sentry
  // fallback runs.
  const ruleKeyRoute = Object.hasOwn(RULE_KEY_ROUTES, notification.type)
    ? RULE_KEY_ROUTES[notification.type]
    : undefined;
  if (ruleKeyRoute != null) return ruleKeyRoute;

  // No route exists for this notification's type or target kind. A `charter`
  // action reaches here only with both `charterId` and `groupId` null: a group
  // with no charter runs `handleAct`'s setup mutation instead, and `ActionFeed`
  // disables the card while the WorkGroup query loads. So reaching here with
  // `charter` means the group id was genuinely missing. Report it, and land on
  // `/home` rather than a `NoMatchError` 404.
  captureMessage("Student home: no route for action rule key", {
    level: "warning",
    tags: { ruleKey: notification.type },
    extra: {
      tier: notification.tier,
      actionLabel: notification.action?.label,
    },
  });
  return "/home";
}

// A wrapper around `destinationFor` rather than a stamp in each of its
// branches, so a destination added later inherits the marker automatically
// instead of depending on whoever adds it to remember to stamp it too.
export function routeForAction(
  notification: Notification,
  charterId: string | null | undefined
): string {
  return withNavigationSource(
    destinationFor(notification, charterId),
    "notification",
    notification.type
  );
}
